import { COLORS } from '../../constants/theme';
import { useGatherIntel } from '../../hooks/useGatherIntel';
import { IntelFreshnessIndicator } from '../attack/IntelFreshnessIndicator';
import { buildSurveyRows } from '../../utils/intelPanelData';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import type { SurveyData, IntelReportDto } from '../../types/intel';

const SV_FG = '#c4b078';

interface MobileSurveyCardProps {
  worldId: string;
  playerProvinceId: string;
  targetProvinceId: string;
  data: SurveyData | null;
  report: IntelReportDto | null;
  isLoading: boolean;
  error: string | null;
  /** SF-105: disables the Gather button when the target is protected. */
  disabled?: boolean;
  onResult: (message: string, type: 'success' | 'failure' | 'warning') => void;
  onGathered: () => void;
  /** SF-157: per-report Share/Delete, gated on report.sharing === 'PRIVATE' for Share. Omitted
   *  (undefined/falsy) by Target Finder's existing usage — no icons render there. */
  onShare?: () => void;
  onDelete?: () => void;
  /** SF-157: when true, suppresses only the Gather/Refresh button and its trigger — the
   *  `useGatherIntel` hook itself is still called unconditionally (Rules of Hooks). */
  readOnly?: boolean;
}

const REASON_ID = 'survey-protected-reason';

/**
 * SF-171 Component 9: Survey mobile card. Explicit "informational only" note (doesn't affect
 * attack outcome), building breakdown via the shared `buildSurveyRows()`, and an "Under
 * Construction" list from the (already-correct) `construction` field — no SF-170 dependency.
 */
export function MobileSurveyCard({
  worldId, playerProvinceId, targetProvinceId, data, report, isLoading, error, disabled, onResult, onGathered,
  onShare, onDelete, readOnly,
}: MobileSurveyCardProps) {
  const { gather, state } = useGatherIntel(worldId, playerProvinceId, targetProvinceId, { onResult, onGathered });
  const isGathering = state.SURVEY === 'loading';
  const isDisabled = isGathering || disabled;
  const rows = data ? buildSurveyRows(data.buildings, data.totalLand) : [];

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10, opacity: readOnly ? 0.85 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(122,106,64,0.15)', borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: SV_FG, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Survey</span>
          {readOnly && (
            <span style={{ fontSize: 9, color: COLORS.text.muted, border: `1px solid ${COLORS.border}`, borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase' }}>
              Archived
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {report && <IntelFreshnessIndicator capturedAt={report.capturedAt} />}
          {onShare && report?.sharing === 'PRIVATE' && (
            <button
              type="button"
              aria-label="Share this Survey report"
              onClick={onShare}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.positive, cursor: 'pointer', fontSize: 14 }}
            >
              ⤴
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label="Delete this Survey report"
              onClick={onDelete}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.negative, cursor: 'pointer', fontSize: 14 }}
            >
              🗑
            </button>
          )}
          {data && !readOnly && (
            <button
              type="button"
              aria-label="Refresh Survey intel"
              aria-describedby={disabled ? REASON_ID : undefined}
              onClick={() => gather('SURVEY')}
              disabled={isDisabled}
              style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: SV_FG, cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: 16, opacity: isDisabled ? 0.4 : 1 }}
            >
              ⟳
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {isLoading && !data && (
          <p style={{ color: COLORS.text.muted, fontSize: 12, margin: 0 }}>Loading...</p>
        )}
        {error && !data && (
          <p style={{ color: COLORS.negative, fontSize: 12, margin: 0 }}>Failed to load Survey intel.</p>
        )}
        {!isLoading && !error && !data && (
          <>
            <p style={{ color: COLORS.text.muted, fontSize: 12, marginTop: 0 }}>
              Reveals the target's building breakdown and construction queue.
            </p>
            <button
              type="button"
              onClick={() => gather('SURVEY')}
              disabled={isDisabled}
              aria-describedby={disabled ? REASON_ID : undefined}
              style={{
                width: '100%', minHeight: 44, borderRadius: 6, border: 'none',
                background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700,
                cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1,
              }}
            >
              {isGathering ? 'Sending thieves...' : 'Gather Survey'}
            </button>
            {disabled && (
              <p id={REASON_ID} style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 6 }}>
                Target is protected — thievery-based intel unavailable.
              </p>
            )}
          </>
        )}
        {data && (
          <>
            <p style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 0, marginBottom: 10, fontStyle: 'italic' }}>
              Informational only — doesn't affect your attack outcome.
            </p>

            {data.networth != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', marginBottom: 6 }}>
                <span style={{ color: COLORS.text.tertiary, fontSize: 12 }}>Networth</span>
                <span style={{ color: COLORS.gold, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatNumber(data.networth)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: COLORS.text.tertiary, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Buildings</span>
              <span style={{ color: COLORS.text.muted, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{formatNumber(data.totalLand)} acres</span>
            </div>
            {rows.map((b) => (
              <div key={b.key} style={{ display: 'flex', padding: '2px 0' }}>
                <span style={{ color: COLORS.text.secondary, fontSize: 12, flex: 1 }}>{b.label}</span>
                <span style={{ color: COLORS.text.primary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", width: 42, textAlign: 'right' }}>
                  {formatNumber(b.count)}
                </span>
                <span style={{ color: COLORS.text.muted, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", width: 52, textAlign: 'right' }}>
                  ({b.pct}%)
                </span>
              </div>
            ))}

            {data.construction.length > 0 && (
              <>
                <div style={{ color: COLORS.text.tertiary, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 4px', borderTop: `1px solid ${COLORS.border}`, paddingTop: 8 }}>
                  Under Construction
                </div>
                {data.construction.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ color: COLORS.text.secondary, fontSize: 12 }}>{formatEnumLabel(c.buildingType)}</span>
                    <span style={{ color: COLORS.text.primary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatNumber(c.acres)} acres · {formatDuration(c.ticksRemaining)}
                    </span>
                  </div>
                ))}
              </>
            )}

            {disabled && (
              <p id={REASON_ID} style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 10 }}>
                Target is protected — thievery-based intel unavailable.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
